/**
 * Food Safety Services page content (/food-safety-services).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Food Safety Services (Main Page) — SEO Optimized 2026" requirement document
 * (MBA_MERN_Solutions/Food Safety Services.docx). Do not reword, shorten or
 * extend this copy — update the document first.
 *
 * NOTE the document also carries a per-service SEO Title and Meta Description
 * for each of the five services. Those are page metadata for future dedicated
 * sub-pages, not body copy, so they are NOT rendered here — only this page's
 * own title and description are used, from `foodSafetySeo` below.
 *
 * `listLabel` and `benefitsLabel` are per service on purpose: the document
 * introduces each list with different wording ("Our HACCP Consultancy
 * Includes:", "Our Halal Certification Support:", "Our FSSC 22000 Services:"
 * ...). Those are not interchangeable and must not be collapsed into one label.
 *
 * `logoKey` values resolve against src/data/certificationLogos.js — all five
 * services have a real mark. `image` paths point at the illustrations already
 * in public/assets/certification-process. No new artwork was introduced.
 */

export const foodSafetySeo = {
  title:
    "Food Safety Certification Consultancy | HACCP, Halal, FSSC 22000 & BRCGS | MBA Solutions",
  description:
    "MBA Solutions provides food safety consultancy services including HACCP, Halal Certification, FSSC 22000, BRCGS Food Safety and GMP/GHP implementation support for food businesses.",
  slug: "/food-safety-services",
};

export const heroContent = {
  h1: "Professional Food Safety Certification Consultancy Services",
  subtitle: "Building Safer Food Businesses Through International Standards",
  paragraphs: [
    "MBA Solutions helps food manufacturers, processors, exporters and food-related organizations develop effective food safety management systems and achieve internationally recognized certifications.",
    "Our food safety experts support businesses from initial assessment and documentation to implementation, training and certification audit preparation.",
    "We help organizations improve food safety controls, meet buyer requirements and build trust with customers and global markets.",
  ],
};

export const servicesContent = {
  title: "Our Food Safety Services",
  services: [
    {
      id: "haccp",
      logoKey: "haccp",
      title: "HACCP Certification",
      headline: "HACCP Certification – Building Effective Food Safety Controls",
      paragraphs: [
        "HACCP (Hazard Analysis and Critical Control Points) is a globally recognized preventive food safety system designed to identify, evaluate and control food safety hazards throughout the production process.",
        "MBA Solutions helps organizations implement HACCP principles to improve food safety performance, reduce risks and meet industry requirements.",
      ],
      listLabel: "Our HACCP Consultancy Includes:",
      items: [
        "HACCP gap assessment",
        "Hazard analysis",
        "Critical control point identification",
        "HACCP plan development",
        "Documentation support",
        "Employee awareness training",
        "Implementation guidance",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of HACCP Certification:",
      benefits: [
        "Improved food safety management",
        "Reduced contamination risks",
        "Better regulatory compliance",
        "Increased customer confidence",
        "Improved market opportunities",
      ],
    },
    {
      id: "halal",
      logoKey: "halal",
      title: "Halal Certification",
      headline: "Halal Certification – Expanding Your Business Into Halal Markets",
      paragraphs: [
        "Halal certification demonstrates that products, processes and operations comply with recognized halal requirements.",
        "MBA Solutions supports food manufacturers and businesses in preparing for halal certification by developing compliant systems and ensuring readiness for certification assessment.",
      ],
      listLabel: "Our Halal Certification Support:",
      items: [
        "Halal requirement assessment",
        "Process evaluation",
        "Documentation preparation",
        "Compliance guidance",
        "Internal review support",
        "Certification preparation",
      ],
      benefitsLabel: "Benefits of Halal Certification:",
      benefits: [
        "Access to halal markets",
        "Increased consumer trust",
        "Improved product credibility",
        "Better export opportunities",
        "Stronger brand reputation",
      ],
    },
    {
      id: "fssc-22000",
      logoKey: "fssc-22000",
      title: "FSSC 22000 Certification",
      headline:
        "FSSC 22000 Certification – Global Food Safety Management Excellence",
      paragraphs: [
        "FSSC 22000 is a globally recognized food safety certification scheme designed for organizations involved in food manufacturing and supply chains.",
        "MBA Solutions helps businesses implement FSSC 22000 requirements and prepare for successful certification.",
      ],
      listLabel: "Our FSSC 22000 Services:",
      items: [
        "Gap assessment",
        "Food safety system development",
        "Documentation preparation",
        "Risk assessment support",
        "Training programs",
        "Internal audit support",
        "Certification audit preparation",
      ],
      benefitsLabel: "Benefits of FSSC 22000:",
      benefits: [
        "International recognition",
        "Strong food safety controls",
        "Improved buyer confidence",
        "Better supply chain acceptance",
        "Access to global markets",
      ],
    },
    {
      id: "brcgs",
      logoKey: "brcgs",
      title: "BRCGS Food Safety Certification",
      headline: "BRCGS Food Safety – Meeting Global Food Industry Requirements",
      paragraphs: [
        "BRCGS Food Safety is a widely recognized standard used by food manufacturers and suppliers to demonstrate product safety, quality and operational control.",
        "MBA Solutions helps organizations prepare their systems according to BRCGS requirements.",
      ],
      listLabel: "Our BRCGS Consultancy Includes:",
      items: [
        "Current system evaluation",
        "Gap analysis",
        "Documentation development",
        "Food safety improvement planning",
        "Staff training",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of BRCGS Certification:",
      benefits: [
        "Meet retailer requirements",
        "Improve food safety performance",
        "Strengthen supplier credibility",
        "Reduce operational risks",
        "Support international trade",
      ],
    },
    {
      id: "gmp-ghp",
      logoKey: "gmp",
      title: "GMP/GHP Implementation",
      headline: "GMP & GHP – Strengthening Food Safety Foundations",
      paragraphs: [
        "Good Manufacturing Practices (GMP) and Good Hygiene Practices (GHP) provide the foundation for safe and consistent food production.",
        "MBA Solutions helps organizations establish effective hygiene and production practices that support food safety compliance.",
      ],
      listLabel: "Our GMP/GHP Support:",
      items: [
        "Facility assessment",
        "Hygiene practice review",
        "Production process evaluation",
        "Employee training",
        "Documentation support",
        "Compliance improvement",
      ],
      benefitsLabel: "Benefits of GMP/GHP:",
      benefits: [
        "Improved hygiene standards",
        "Better production control",
        "Reduced contamination risks",
        "Improved product quality",
        "Stronger food safety culture",
      ],
    },
  ],
};

export const processContent = {
  title: "Our Food Safety Certification Process",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "Step 1: Initial Consultation",
      description:
        "Understanding your business, products and certification requirements.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "Step 2: Gap Assessment",
      description:
        "Identifying gaps against food safety standard requirements.",
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      title: "Step 3: Documentation",
      description: "Preparing required procedures, records and systems.",
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      title: "Step 4: Implementation",
      description: "Supporting your team in applying food safety practices.",
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      title: "Step 5: Internal Audit",
      description: "Checking readiness before certification assessment.",
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      title: "Step 6: Certification Support",
      description:
        "Helping your organization complete the certification journey.",
    },
  ],
};

export const industriesContent = {
  title: "Industries We Support",
  intro: "MBA Solutions provides food safety consultancy for:",
  industries: [
    { icon: "manufacturing", name: "Food Manufacturing" },
    { icon: "beverage", name: "Beverage Industry" },
    { icon: "rice", name: "Rice Mills" },
    { icon: "dairy", name: "Dairy Industry" },
    { icon: "meat", name: "Meat & Poultry" },
    { icon: "hospitality", name: "Restaurants & Hospitality" },
    { icon: "export", name: "Food Exporters" },
    { icon: "packaging", name: "Packaging & Supply Chain Businesses" },
  ],
};

export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  reasons: [
    "Food Safety Expertise",
    "Practical Implementation Approach",
    "Complete Certification Support",
    "Industry-Specific Solutions",
    "International Standards Knowledge",
  ],
};

export const finalCtaContent = {
  title: "Improve Your Food Safety Standards Today",
  paragraph:
    "Partner with MBA Solutions to achieve certification, strengthen compliance and build customer confidence.",
  cta: { label: "Request Food Safety Consultation", to: "/contact" },
};
